function bindFumiSizeChartTabs(root) {
  root.querySelectorAll('[data-fumi-size-chart-wrap]').forEach((wrap) => {
    const tabs = wrap.querySelectorAll('[data-fumi-unit]');
    const panels = wrap.querySelectorAll('[data-fumi-size-chart-panel]');

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const unit = tab.dataset.fumiUnit;
        if (!unit) return;

        tabs.forEach((btn) => {
          const isActive = btn === tab;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        panels.forEach((panel) => {
          const isActive = panel.dataset.fumiSizeChartPanel === unit;
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);
        });
      });
    });
  });
}

class FumiMainProduct {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.section;
    this.buyButtons = this.root.querySelector('[data-fumi-buy-buttons]');
    this.bisButton =
      this.root.querySelector('[data-fumi-bis-btn]') ||
      this.root.querySelector('#BIS_trigger');
    this.bisButtonLabel =
      this.bisButton?.dataset.fumiBisLabel ||
      this.bisButton?.textContent.trim() ||
      'NOTIFY WHEN BACK IN STOCK';
    this.addButtonLabel =
      this.root.querySelector('[data-fumi-add-label]')?.dataset.defaultLabel ||
      this.buyButtons?.dataset.defaultLabel ||
      'Add to cart ·';
    this.preorderButtonLabel =
      this.root.querySelector('[data-fumi-add-label]')?.dataset.preorderLabel ||
      this.buyButtons?.dataset.preorderLabel ||
      'Preorder ·';
    this.soldOutButtonLabel =
      this.root.querySelector('[data-fumi-add-label]')?.dataset.soldOutLabel ||
      'Sold out ·';
    const variantSelects = this.root.querySelector('variant-selects');
    const stoqIds = [
      root.dataset.stoqVariantIds || '',
      variantSelects?.dataset.stoqPreorderIds || '',
    ].join(',');

    this.stoqVariantIds = new Set(
      stoqIds
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
    this.preorderByVariantId = new Map();
    this.sellingPlanByVariantId = new Map();
    this.bindAccordions();
    this.bindMobileGallery();
    this.bindZoom();
    this.bindSizeModal();
    bindFumiSizeChartTabs(this.root);
    this.bindVariantStyles();
    this.bindFitQuiz();
    this.bindFinishLookForms();
    this.bindVariantUpdates();
    this.bindStoqIntegration();
    this.bindBisLabelGuard();
  }

  getCurrentVariantId() {
    return this.buyButtons?.querySelector('.product-variant-id')?.value || null;
  }

  getVariantFromJson(variantId) {
    const script = this.root.querySelector('variant-selects script[type="application/json"]');
    if (!script) return null;

    try {
      const variants = JSON.parse(script.textContent);
      return variants.find((entry) => String(entry.id) === String(variantId)) || null;
    } catch {
      return null;
    }
  }

  getVariantState(variantId, available) {
    const variant = this.getVariantFromJson(variantId);
    return {
      id: variantId,
      available: typeof available === 'boolean' ? available : !!variant?.available,
    };
  }

  getSellingPlan(variantId) {
    const id = String(variantId);
    if (this.sellingPlanByVariantId.has(id)) {
      return this.sellingPlanByVariantId.get(id);
    }

    if (!window._RestockRocket?.getSellingPlan) {
      return null;
    }

    try {
      const plan =
        window._RestockRocket.getSellingPlan(id) ||
        window._RestockRocket.getSellingPlan(Number(id));
      if (plan) this.sellingPlanByVariantId.set(id, plan);
      return plan || null;
    } catch {
      return null;
    }
  }

  cachePreorderState(variantId, isPreorder) {
    if (typeof isPreorder !== 'boolean') return;
    this.preorderByVariantId.set(String(variantId), isPreorder);
  }

  isStoqPreorder(variantId) {
    const id = String(variantId);

    if (this.preorderByVariantId.has(id)) {
      return this.preorderByVariantId.get(id);
    }

    const plan = this.getSellingPlan(id);
    if (plan && plan.enabled !== false) {
      this.cachePreorderState(id, true);
      return true;
    }

    if (this.stoqVariantIds.has(id)) {
      this.cachePreorderState(id, true);
      return true;
    }

    return false;
  }

  getButtonState(variant) {
    const sellingPlan = this.getSellingPlan(variant.id);
    const isPreorder = sellingPlan !== null;
    const isAvailable = !!variant.available;
    const isSoldOutNotify = !sellingPlan && !isAvailable;
    const showAddEnabled = isPreorder || isAvailable;
    const showAddButton = showAddEnabled || isSoldOutNotify;

    let label = this.addButtonLabel;
    if (isPreorder) {
      label = this.getAddButtonLabel(true, sellingPlan);
    } else if (isAvailable) {
      label = this.addButtonLabel;
    } else {
      label = this.soldOutButtonLabel;
    }

    return {
      isPreorder,
      isAvailable,
      isSoldOutNotify,
      showAddEnabled,
      showAddButton,
      showBis: isSoldOutNotify,
      label,
    };
  }

  setBisVisibility(show) {
    const bisBtn = this.bisButton;
    if (!bisBtn) return;

    this.ensureBisButtonLabel();
    bisBtn.hidden = !show;
    if (show) {
      bisBtn.style.setProperty('display', 'block', 'important');
    } else {
      bisBtn.style.setProperty('display', 'none', 'important');
    }
  }

  ensureBisButtonLabel() {
    if (!this.bisButton || !this.bisButtonLabel) return;
    if (this.bisButton.textContent.trim() !== this.bisButtonLabel) {
      this.bisButton.textContent = this.bisButtonLabel;
    }
    this.bisButton.setAttribute('aria-label', this.bisButtonLabel);
  }

  bindBisLabelGuard() {
    if (!this.bisButton) return;

    this.ensureBisButtonLabel();
    this.bisLabelObserver = new MutationObserver(() => {
      this.ensureBisButtonLabel();
    });
    this.bisLabelObserver.observe(this.bisButton, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  hideStoqInjectedButtons() {
    document.querySelectorAll('.restock-rocket-button-product').forEach((btn) => {
      btn.style.setProperty('display', 'none', 'important');
    });
  }

  preloadStoqPreorderStates() {
    this.getAllVariants().forEach((variant) => {
      const plan = this.getSellingPlan(variant.id);
      if (plan && plan.enabled !== false) {
        this.cachePreorderState(variant.id, true);
        return;
      }
      if (this.stoqVariantIds.has(String(variant.id))) {
        this.cachePreorderState(variant.id, true);
      }
    });
  }

  getAllVariants() {
    const script = this.root.querySelector('variant-selects script[type="application/json"]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  getAddButtonLabel(isPreorder, sellingPlan) {
    if (!isPreorder) return this.addButtonLabel;

    const preorderText = sellingPlan?.preorder_button_text;
    if (!preorderText) return this.preorderButtonLabel;

    return preorderText.includes('·') ? preorderText : `${preorderText} ·`;
  }

  applyAddButtonLabel(variant) {
    const label = this.buyButtons?.querySelector('[data-fumi-add-label]');
    if (!label || !variant?.id) return;

    label.textContent = this.getButtonState(variant).label;
  }

  scheduleLabelApply(variant) {
    if (!variant?.id) return;

    const apply = () => {
      this.applyAddButtonLabel(variant);
      const state = this.getButtonState(variant);
      this.setBisVisibility(state.showBis);
      this.hideStoqInjectedButtons();
    };

    apply();
    window.setTimeout(apply, 300);
  }

  bindStoqIntegration() {
    window.addEventListener('stoq:variant:changed', (event) => {
      const { variantId, available, hasSellingPlan } = event.detail || {};
      if (!variantId) return;

      if (typeof hasSellingPlan === 'boolean') {
        this.cachePreorderState(variantId, hasSellingPlan);
      }

      const variant = this.getVariantState(variantId, available);
      this.updateBuyButtons(variant);
      this.scheduleLabelApply(variant);
    });

    const variantSelects = this.root.querySelector('variant-selects');
    variantSelects?.addEventListener('change', () => {
      window.setTimeout(() => {
        const currentVariant = variantSelects.currentVariant;
        if (!currentVariant) return;
        this.updateBuyButtons(currentVariant);
        this.scheduleLabelApply(currentVariant);
      }, 50);
    });

    window.addEventListener('stoq:loaded', () => {
      this.preloadStoqPreorderStates();
      const variantId = this.getCurrentVariantId();
      if (!variantId) return;
      const variant = this.getVariantState(variantId);
      this.updateBuyButtons(variant);
      this.scheduleLabelApply(variant);
    });

    const variantId = this.getCurrentVariantId();
    if (variantId) {
      const variant = this.getVariantState(variantId);
      requestAnimationFrame(() => {
        this.updateBuyButtons(variant);
        this.scheduleLabelApply(variant);
      });
    }
  }

  updateBuyButtons(variant, html) {
    if (!this.buyButtons || !variant?.id) return;

    const state = this.getButtonState(variant);
    const addBtn = this.buyButtons.querySelector('.fumi-add-btn');
    let priceEl = this.buyButtons.querySelector('[data-fumi-add-price]');
    const variantInput = this.buyButtons.querySelector('.product-variant-id');
    const priceSource = html?.querySelector('[data-fumi-add-price]');
    const label = this.buyButtons.querySelector('[data-fumi-add-label]');

    if (addBtn) {
      addBtn.hidden = !state.showAddButton;
      addBtn.toggleAttribute('disabled', !state.showAddEnabled);
      addBtn.removeAttribute('aria-disabled');
    }

    if (variantInput) {
      variantInput.disabled = !state.showAddEnabled;
      variantInput.value = variant.id;
    }

    if (label) {
      label.textContent = state.label;
    }

    if (!priceEl && addBtn) {
      const spinner = addBtn.querySelector('.loading__spinner');
      priceEl = document.createElement('span');
      priceEl.className = 'fumi-num fumi-add-btn__price';
      priceEl.setAttribute('data-fumi-add-price', '');
      if (spinner) {
        addBtn.insertBefore(priceEl, spinner);
      } else {
        addBtn.appendChild(priceEl);
      }
    }

    if (priceEl) {
      if (priceSource?.textContent?.trim()) {
        priceEl.textContent = priceSource.textContent.trim();
      }
      priceEl.hidden = false;
    }

    this.setBisVisibility(state.showBis);
    this.hideStoqInjectedButtons();
  }

  syncBuyButtonsFromSource(_source, variant, html) {
    if (!variant?.id) return;
    this.updateBuyButtons(variant, html);
  }

  bindVariantUpdates() {
    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data.sectionId !== this.sectionId || !event.data.variant) return;

      const variant = event.data.variant;
      const html = event.data.html;

      this.updateBuyButtons(variant, html);
      this.scheduleLabelApply(variant);

      const variantSelects = this.root.querySelector('variant-selects');
      if (variantSelects) this.refreshVariantOptionStyles(variantSelects);
    });
  }

  bindAccordions() {
    this.root.querySelectorAll('[data-fumi-accordion]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const panelId = btn.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) return;

        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        panel.hidden = expanded;
      });
    });
  }

  bindMobileGallery() {
    const gallery = this.root.querySelector('[data-fumi-gallery-swiper]');
    if (!gallery) return;

    const pagination = gallery.querySelector('.fumi-gallery__pagination');
    const mobileQuery = window.matchMedia('(max-width: 766px)');
    let swiper = null;

    const syncSwiper = () => {
      if (mobileQuery.matches && !swiper && typeof window.Swiper === 'function') {
        gallery.classList.add('swiper');
        swiper = new window.Swiper(gallery, {
          slidesPerView: 1,
          spaceBetween: 10,
          watchOverflow: true,
          pagination: {
            el: pagination,
            clickable: true,
          },
        });
        this.gallerySwiper = swiper;
        return;
      }

      if (!mobileQuery.matches && swiper) {
        swiper.destroy(true, true);
        swiper = null;
        this.gallerySwiper = null;
        gallery.classList.remove('swiper');
      }
    };

    syncSwiper();
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', syncSwiper);
    } else {
      mobileQuery.addListener(syncSwiper);
    }
  }

  bindZoom() {
    const zoomModal = this.root.querySelector('[data-fumi-zoom-modal]');
    if (!zoomModal) return;

    const slides = Array.from(zoomModal.querySelectorAll('[data-fumi-zoom-slide]'));
    const prevBtn = zoomModal.querySelector('[data-fumi-zoom-prev]');
    const nextBtn = zoomModal.querySelector('[data-fumi-zoom-next]');
    const closeBtn = zoomModal.querySelector('[data-fumi-zoom-close]');
    const counter = zoomModal.querySelector('[data-fumi-zoom-counter]');
    let currentIndex = 0;

    if (!slides.length) return;

    const pauseSlideMedia = (slide) => {
      slide?.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });
      slide?.querySelectorAll('iframe').forEach((iframe) => {
        const src = iframe.getAttribute('src');
        if (src) iframe.setAttribute('src', src.replace('autoplay=1', 'autoplay=0'));
      });
    };

    const goTo = (index) => {
      currentIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === currentIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        if (!active) pauseSlideMedia(slide);
      });
      if (counter) counter.textContent = `${currentIndex + 1} / ${slides.length}`;
    };

    const open = (mediaId) => {
      const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      goTo(index >= 0 ? index : 0);
      zoomModal.hidden = false;
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      slides.forEach((slide) => pauseSlideMedia(slide));
      zoomModal.hidden = true;
      document.body.style.overflow = '';
    };

    this.root.querySelectorAll('[data-fumi-zoom]').forEach((btn) => {
      btn.addEventListener('click', () => {
        open(btn.dataset.mediaId);
      });
    });

    prevBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      goTo(currentIndex - 1);
    });

    nextBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      goTo(currentIndex + 1);
    });

    closeBtn?.addEventListener('click', close);
    zoomModal.addEventListener('click', (e) => {
      if (e.target === zoomModal) close();
    });

    this.zoomKeyHandler = (e) => {
      if (zoomModal.hidden) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') goTo(currentIndex - 1);
      if (e.key === 'ArrowRight') goTo(currentIndex + 1);
    };
    document.addEventListener('keydown', this.zoomKeyHandler);
  }

  bindSizeModal() {
    const modal = this.root.querySelector('[data-fumi-size-modal]');
    if (!modal) return;

    const closeBtn = modal.querySelector('[data-fumi-size-modal-close]');
    const open = () => {
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      modal.hidden = true;
      document.body.style.overflow = '';
    };

    this.root.querySelectorAll('[data-fumi-open-size-modal]').forEach((btn) => {
      btn.addEventListener('click', open);
    });

    closeBtn?.addEventListener('click', close);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  bindVariantStyles() {
    const variantSelects = this.root.querySelector('variant-selects');
    if (!variantSelects) return;

    const refresh = () => this.refreshVariantOptionStyles(variantSelects);

    variantSelects.addEventListener('change', refresh);
    refresh();
  }

  refreshVariantOptionStyles(variantSelects) {
    variantSelects.querySelectorAll('.fumi-swatch').forEach((label) => {
      const input = label.querySelector('input');
      label.classList.toggle('is-selected', input?.checked);
    });
    variantSelects.querySelectorAll('.fumi-size-wrap').forEach((wrap) => {
      const input = wrap.querySelector('input');
      wrap.querySelector('.fumi-size')?.classList.toggle('is-selected', input?.checked);
    });
    this.syncOptionAvailability(variantSelects);
    this.syncFitAvatarColor(variantSelects);
  }

  syncOptionAvailability(variantSelects) {
    const variants = this.getAllVariants();
    if (!variants.length) return;

    const fieldsets = [...variantSelects.querySelectorAll('fieldset.fumi-option')];
    const selected = {};

    fieldsets.forEach((fieldset, index) => {
      const position = fieldset.dataset.optionPosition || String(index + 1);
      const checked = fieldset.querySelector('input:checked');
      if (checked) selected[position] = checked.value;
    });

    fieldsets.forEach((fieldset, index) => {
      const position = fieldset.dataset.optionPosition || String(index + 1);

      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const available = variants.some((variant) => {
          if (String(variant[`option${position}`]) !== String(input.value)) return false;
          if (!variant.available) return false;

          return Object.entries(selected).every(([pos, value]) => {
            if (String(pos) === String(position)) return true;
            return String(variant[`option${pos}`]) === String(value);
          });
        });

        this.applyOptionAvailability(input, available);
      });
    });
  }

  applyOptionAvailability(input, available) {
    const wrap = input.closest('.fumi-size-wrap, .fumi-swatch');
    if (available) {
      input.removeAttribute('data-unavailable');
    } else {
      input.setAttribute('data-unavailable', 'true');
    }

    if (wrap?.classList.contains('fumi-size-wrap')) {
      wrap.classList.toggle('fumi-size-wrap-unavailable', !available);
      wrap.querySelector('.fumi-size')?.classList.toggle('is-sold-out', !available);

      const suffix = available
        ? input.checked
          ? ', selected'
          : ''
        : ', sold out';
      input.setAttribute('aria-label', `${input.name} ${input.value}${suffix}`);
    }

    if (wrap?.classList.contains('fumi-swatch')) {
      wrap.classList.toggle('is-unavailable', !available);
    }
  }

  syncFitAvatarColor(variantSelects) {
    const activeColorInput = variantSelects.querySelector(
      '.fumi-option--color .fumi-swatch__input:checked'
    );
    const activeSwatch = activeColorInput
      ?.closest('.fumi-swatch')
      ?.querySelector('.fumi-swatch__dot');

    this.root.querySelectorAll('[data-fumi-active-variant-color]').forEach((avatar) => {
      const swatchColor = activeSwatch?.style.backgroundColor || '';
      const swatchImage = activeSwatch?.style.backgroundImage || '';

      avatar.style.backgroundColor = swatchColor || avatar.dataset.fumiFallbackColor || '';
      avatar.style.backgroundImage = swatchImage || 'none';
      avatar.style.backgroundSize = swatchImage ? 'cover' : '';
      avatar.style.backgroundPosition = swatchImage ? 'center' : '';
    });
  }

  bindFitQuiz() {
    const quiz = this.root.querySelector('[data-fumi-fit-quiz]');
    if (!quiz) return;

    const result = quiz.querySelector('[data-fumi-quiz-result]');
    const resultText = quiz.querySelector('[data-fumi-quiz-result-text]');
    const selectBtn = quiz.querySelector('[data-fumi-quiz-select]');
    const selectLabel = quiz.querySelector('[data-fumi-quiz-select-label]');
    const state = { height: null, heightNote: '', usual: null, bust: null };

    quiz.querySelectorAll('[data-fumi-quiz-group]').forEach((group) => {
      group.querySelectorAll('[data-fumi-quiz-option]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const key = group.dataset.fumiQuizGroup;
          state[key] = btn.dataset.fumiQuizValue;
          if (key === 'height') {
            state.heightNote = btn.dataset.fumiQuizNote || '';
          }
          group.querySelectorAll('[data-fumi-quiz-option]').forEach((el) => el.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          this.updateFitQuizResult(quiz, result, resultText, selectBtn, selectLabel, state);
        });
      });
    });

    selectBtn?.addEventListener('click', () => {
      const size = selectBtn.dataset.recommendedSize;
      if (!size) return;
      this.selectFitQuizSize(size);
    });
  }

  selectFitQuizSize(size) {
    const sizeInput = this.root.querySelector(`.fumi-size__input[value="${CSS.escape(size)}"]`);
    if (sizeInput && !sizeInput.disabled) {
      sizeInput.checked = true;
      sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateFitQuizResult(quiz, result, resultText, selectBtn, selectLabel, state) {
    if (!result || !resultText) return;

    if (!state.height || !state.usual || !state.bust) {
      result.hidden = true;
      if (selectBtn) selectBtn.hidden = true;
      return;
    }

    let recommended = state.usual;
    if (state.bust === 'fuller') {
      const sizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
      const idx = sizes.indexOf(state.usual);
      if (idx >= 0 && idx < sizes.length - 1) recommended = sizes[idx + 1];
    }

    const template = quiz.dataset.resultTemplate || "We'd start you in a size [size] — on you, [note].";
    const note = state.heightNote || '';
    const parts = template.split('[size]');
    const beforeSize = parts[0] || '';
    const afterSizeParts = (parts[1] || '').split('[note]');
    const betweenSizeAndNote = afterSizeParts[0] || '';
    const afterNote = afterSizeParts[1] || '';

    resultText.textContent = '';
    if (beforeSize) resultText.append(document.createTextNode(beforeSize));

    const sizeWrap = document.createElement('span');
    sizeWrap.className = 'fumi-quiz__result-size';
    const sizeNum = document.createElement('span');
    sizeNum.className = 'fumi-num';
    sizeNum.textContent = `size ${recommended}`;
    sizeWrap.appendChild(sizeNum);
    resultText.appendChild(sizeWrap);

    if (betweenSizeAndNote) resultText.append(document.createTextNode(betweenSizeAndNote));

    if (note) {
      const noteSpan = document.createElement('span');
      noteSpan.className = 'fumi-quiz__result-note';
      noteSpan.textContent = note;
      resultText.appendChild(noteSpan);
    }

    if (afterNote) resultText.append(document.createTextNode(afterNote));

    if (selectBtn && selectLabel) {
      const selectTemplate = quiz.dataset.selectLabel || 'Select size [size]';
      selectLabel.textContent = selectTemplate.replace('[size]', recommended);
      selectBtn.dataset.recommendedSize = recommended;
      selectBtn.hidden = false;
    }

    result.hidden = false;
  }

  bindFinishLookForms() {
    this.root.querySelectorAll('[data-fumi-finish-form]').forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('button[type="submit"]');
        if (!btn || btn.disabled) return;

        btn.disabled = true;
        const formData = new FormData(form);

        try {
          if (typeof window.fumiAddToCart === 'function') {
            await window.fumiAddToCart({ formData, triggerEl: btn });
          } else {
            const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

            if (cart?.getSectionsToRender) {
              formData.append(
                'sections',
                cart.getSectionsToRender().map((section) => section.id).join(',')
              );
              formData.append('sections_url', window.location.pathname);
              cart.setActiveElement?.(document.activeElement);
            }

            const config =
              typeof fetchConfig === 'function'
                ? fetchConfig('javascript')
                : { method: 'POST', headers: { Accept: 'application/javascript' } };
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];
            config.body = formData;

            const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}`, config);
            const data = await response.json();
            if (data.status) throw new Error(data.description || data.message);

            const didRenderDrawer = !!(cart?.renderContents && data.sections);
            if (didRenderDrawer) {
              cart.renderContents(data);
            } else if (window.publish && window.PUB_SUB_EVENTS) {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                source: 'fumi-finish-look',
                productVariantId: formData.get('id'),
                cartData: data,
              });
            }
          }
        } catch (err) {
          console.error(err);
        } finally {
          btn.disabled = false;
        }
      });
    });
  }
}

window.FumiMainProduct = FumiMainProduct;

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-fumi-main-product]').forEach((root) => new FumiMainProduct(root));
});

if (Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-fumi-main-product]');
    if (root) new FumiMainProduct(root);
  });
}
